<script setup lang="ts">
import type { SelectOption } from '@/js/account/components/base/BaseSelect.vue';
import {
  computed,
  onBeforeUnmount,
  onMounted,
  ref,
  useId,
} from 'vue';
import BaseBottomSheet from '@/js/account/components/base/BaseBottomSheet.vue';
import BaseTitle from '@/js/account/components/base/BaseTitle.vue';
import SortSelectOptionList from '@/js/account/components/library/SortSelectOptionList.vue';
import { classNames } from '@/js/lib/bem';

const props = withDefaults(
  defineProps<{
    modelValue?: string
    options: SelectOption[]
    label?: string
    name?: string
  }>(),
  {
    modelValue: '',
    label: 'Sort by',
    name: 'sort-by',
  },
);

const emit = defineEmits<{
  'update:modelValue': [value: string]
}>();

const cn = classNames('sort-select');
const listboxId = useId();
const rootRef = ref<HTMLElement | null>(null);
const isDropdownOpen = ref(false);
const isSheetOpen = ref(false);

const selectedLabel = computed(
  () =>
    props.options.find(option => option.value === props.modelValue)?.label
    ?? '',
);

function toggleDropdown(): void {
  isDropdownOpen.value = !isDropdownOpen.value;
}

function openSheet(): void {
  isSheetOpen.value = true;
}

function selectOption(value: string): void {
  emit('update:modelValue', value);
  isDropdownOpen.value = false;
  isSheetOpen.value = false;
}

function onDocumentClick(event: MouseEvent): void {
  if (!rootRef.value?.contains(event.target as Node)) {
    isDropdownOpen.value = false;
  }
}

function onDocumentKeydown(event: KeyboardEvent): void {
  if (event.key === 'Escape') {
    isDropdownOpen.value = false;
  }
}

onMounted(() => {
  document.addEventListener('click', onDocumentClick);
  document.addEventListener('keydown', onDocumentKeydown);
});

onBeforeUnmount(() => {
  document.removeEventListener('click', onDocumentClick);
  document.removeEventListener('keydown', onDocumentKeydown);
});
</script>

<template>
  <div :class="cn()">
    <input type="hidden" :name="name" :value="modelValue">

    <button
      type="button"
      :class="cn('mobile-trigger', { open: isSheetOpen })"
      :aria-expanded="isSheetOpen"
      aria-haspopup="listbox"
      :aria-controls="listboxId"
      :aria-label="`${label}: ${selectedLabel}`"
      @click="openSheet"
    >
      {{ label }}: {{ selectedLabel }}
    </button>

    <BaseBottomSheet v-model:open="isSheetOpen" :title="label">
      <SortSelectOptionList
        :listbox-id="listboxId"
        :label="label"
        :options="options"
        :model-value="modelValue"
        @select="selectOption"
      />
    </BaseBottomSheet>

    <div ref="rootRef" :class="cn('desktop')">
      <button
        type="button"
        :class="cn('trigger', { open: isDropdownOpen })"
        :aria-expanded="isDropdownOpen"
        aria-haspopup="listbox"
        :aria-controls="listboxId"
        @click="toggleDropdown"
      >
        {{ label }}: {{ selectedLabel }}
      </button>

      <div v-if="isDropdownOpen" :class="cn('menu')">
        <BaseTitle size="sm" :class="cn('menu-title')">
          {{ label }}
        </BaseTitle>
        <hr :class="cn('divider')">

        <SortSelectOptionList
          :listbox-id="listboxId"
          :label="label"
          :options="options"
          :model-value="modelValue"
          @select="selectOption"
        />
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.sort-select {
  &__mobile-trigger {
    box-sizing: border-box;
    display: none;
    flex: 1;
    gap: 8px;
    align-items: center;
    justify-content: center;
    min-width: 0;
    padding: 10px 16px;
    overflow: hidden;
    color: var(--color-primary);
    font-weight: 400;
    font-size: 14px;
    font-family: var(--font-family-sans);
    line-height: 1.2;
    text-align: center;
    white-space: nowrap;
    text-overflow: ellipsis;
    background-color: var(--color-white);
    border: none;
    border-radius: 4px;
    box-shadow: var(--shadow);
    cursor: pointer;
    transition: box-shadow 0.2s;
    max-width: max-content;

    &:hover,
    &_open {
      box-shadow: 0 0 12px 2px rgba(214, 214, 214, 0.55);
    }
  }

  &__desktop {
    position: relative;
    display: inline-block;
    width: 100%;
  }

  &__trigger {
    box-sizing: border-box;
    width: 100%;
    padding: 10px 16px;
    color: var(--color-primary);
    font-weight: 400;
    font-size: 14px;
    font-family: var(--font-family-sans);
    line-height: 1.2;
    text-align: left;
    background-color: #fff;
    border: 1px solid var(--base-field-border-color, #e0e0e0);
    border-radius: 4px;
    cursor: pointer;
    transition: border-color 0.2s;

    &:hover,
    &_open {
      border-color: var(--color-primary, #73805b);
    }
  }

  &__menu {
    position: absolute;
    top: calc(100% + 8px);
    left: 0;
    z-index: 20;
    box-sizing: border-box;
    min-width: 100%;
    padding: 24px 0 14px;
    background-color: #fff;
    border-radius: 4px;
    box-shadow: var(--shadow);
  }

  &__menu-title {
    margin: 0;
    padding: 0 24px;
    color: var(--color-text-primary, #101010);
    font-size: 24px;
    font-family: var(--font-family-serif);
    line-height: 1.2;
  }

  &__divider {
    margin: 24px 0 14px;
    border: none;
    border-top: 1px solid var(--base-field-border-color, #e0e0e0);
  }

  @media (max-width: 768px) {
    flex: 1;
    min-width: 0;

    &__mobile-trigger {
      display: inline-flex;
      width: 100%;
      max-width: none;
    }

    &__desktop {
      display: none;
    }
  }
}
</style>
